import type { GridComponent, PredicateModel } from '@syncfusion/ej2-angular-grids';
import type { ColumnFilter, FilterCondition, GridPageRequest, SortDescriptor } from '../models/paged-result';

/** The page size the server-paged grids start with; also the server's default. */
export const DEFAULT_PAGE_SIZE = 20;

/** How a column's values are written on the wire: the server parses them by this. */
export type FieldKind = 'number' | 'text' | 'date';

/** The columns of one grid that may be filtered or sorted on the server, and the kind of each. */
export type GridFields = Record<string, FieldKind>;

/** The part of the grid's state a page request is built from. */
export type GridState = {
    /** Rows skipped before the current page. */
    skip?: number;
    /** Rows on a page. */
    take?: number;
    /** Sort keys as the grid reports them (`direction` is `ascending` / `descending`). */
    sorted?: { name?: string; direction?: string }[];
    /** The grid's flat filter predicates (the same shape `TabFilterStateService` remembers). */
    filters?: PredicateModel[];
};

/** Grid operators the server understands, as the server spells them. */
const SUPPORTED_OPERATORS = new Set([
    'equal', 'notequal', 'lessthan', 'lessthanorequal', 'greaterthan', 'greaterthanorequal',
    'contains', 'startswith', 'endswith', 'doesnotcontain', 'doesnotstartwith', 'doesnotendwith',
    'isnull', 'isnotnull', 'isempty', 'isnotempty'
]);

/** Operators that compare against nothing, so they carry no value. */
const VALUELESS_OPERATORS = new Set(['isnull', 'isnotnull', 'isempty', 'isnotempty']);

/** The whole-column filters the grid keeps per field, in the order they were applied. */
function groupByField(fields: GridFields, predicates: PredicateModel[]): Map<string, PredicateModel[]> {
    const groups = new Map<string, PredicateModel[]>();
    for (const predicate of predicates) {
        const field = predicate.field ?? '';
        if (!(field in fields)) continue; // a stale or hidden column: the server would reject it
        groups.set(field, [...(groups.get(field) ?? []), predicate]);
    }
    return groups;
}

/** Local midnight of the day a date falls on. */
function startOfLocalDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/** Local midnight of the following day (DST-safe: it moves the calendar date, not 24 hours). */
function startOfNextLocalDay(value: Date): Date {
    const next = startOfLocalDay(value);
    next.setDate(next.getDate() + 1);
    return next;
}

function toDate(value: unknown): Date | null {
    const date = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
}

/** A blank, null or undefined value means "no value" (the checkbox list's "(Blanks)" entry). */
function toText(value: unknown): string | null {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
}

/**
 * The server compares dates as UTC instants, but the grid's date filter means "on this calendar day, locally".
 * So a day comparison becomes an explicit local-day range sent as UTC instants.
 */
function dateConditions(operator: string, value: unknown): FilterCondition[] {
    const date = toDate(value);
    if (!date) return [];

    const start = startOfLocalDay(date).toISOString();
    const next = startOfNextLocalDay(date).toISOString();
    switch (operator) {
        case 'equal': return [{ operator: 'between', value: start, valueTo: next }];
        case 'notequal': return [{ operator: 'notbetween', value: start, valueTo: next }];
        case 'greaterthan': return [{ operator: 'greaterthanorequal', value: next }];
        case 'greaterthanorequal': return [{ operator: 'greaterthanorequal', value: start }];
        case 'lessthan': return [{ operator: 'lessthan', value: start }];
        case 'lessthanorequal': return [{ operator: 'lessthan', value: next }];
        default: return [];
    }
}

function toConditions(kind: FieldKind, operator: string, value: unknown): FilterCondition[] {
    if (VALUELESS_OPERATORS.has(operator)) return [{ operator, value: null }];

    if (kind === 'date') return dateConditions(operator, value);

    const text = toText(value);
    // Only a text column can match "no value"; for a number an empty value is not a usable condition.
    if (text === null && !(kind === 'text' && (operator === 'equal' || operator === 'notequal'))) return [];
    return [{ operator, value: text }];
}

/** Turns one column's grid predicates into a server filter. */
function toFilter(kind: FieldKind, field: string, predicates: PredicateModel[]): ColumnFilter | null {
    const conditions: FilterCondition[] = [];

    for (const predicate of predicates) {
        const operator = (predicate.operator ?? '').toLowerCase();

        // "in" / "notin" carry a list: any of them, or none of them.
        if (operator === 'in' || operator === 'notin') {
            const values = Array.isArray(predicate.value) ? predicate.value : [predicate.value];
            const single = operator === 'in' ? 'equal' : 'notequal';
            values.forEach(value => conditions.push(...toConditions(kind, single, value)));
            continue;
        }

        if (!SUPPORTED_OPERATORS.has(operator)) continue;
        conditions.push(...toConditions(kind, operator, predicate.value));
    }

    if (conditions.length === 0) return null;

    // The grid records how a condition joins the previous one on the later predicate; a checkbox filter is all "or".
    const last = predicates[predicates.length - 1];
    const lastOperator = (last.operator ?? '').toLowerCase();
    const logic = lastOperator === 'in' ? 'or'
        : lastOperator === 'notin' ? 'and'
            : (predicates.length > 1 && last.predicate === 'or' ? 'or' : 'and');
    return { field, logic, conditions };
}

/** Turns the grid's filter predicates into the server's column filters. */
export function buildColumnFilters(fields: GridFields, predicates: PredicateModel[] | undefined): ColumnFilter[] {
    const filters: ColumnFilter[] = [];
    groupByField(fields, predicates ?? []).forEach((columnPredicates, field) => {
        const filter = toFilter(fields[field], field, columnPredicates);
        if (filter) filters.push(filter);
    });
    return filters;
}

/** Turns the grid's sort keys into the server's, dropping columns the server does not know. */
export function buildSort(fields: GridFields, sorted: GridState['sorted']): SortDescriptor[] {
    return (sorted ?? [])
        .filter(key => !!key.name && key.name in fields)
        .map(key => ({
            field: key.name as string, // narrowed by the filter above
            direction: (key.direction ?? '').toLowerCase().startsWith('desc') ? 'desc' : 'asc'
        }));
}

/** Builds a page request from the grid's current paging, sorting and filter state. */
export function buildPageRequest(fields: GridFields, state: GridState): GridPageRequest {
    const pageSize = state.take && state.take > 0 ? state.take : DEFAULT_PAGE_SIZE;
    const page = Math.floor((state.skip ?? 0) / pageSize) + 1;
    return { page, pageSize, sort: buildSort(fields, state.sorted), filters: buildColumnFilters(fields, state.filters) };
}

/**
 * The grid's sort keys, most significant first. The `sorted` list in the grid's data request has them the other way
 * round (the grid builds its sort from the last column to the first), so a sort on two columns would be applied with
 * the priorities swapped; the grid's own settings keep them in the order the user chose them.
 */
export function sortKeysOf(grid: GridComponent | undefined): NonNullable<GridState['sorted']> {
    return (grid?.sortSettings.columns ?? []).map(column => ({ name: column.field, direction: column.direction }));
}
